import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { Box, Typography, makeStyles, Slide, Button } from "@material-ui/core";
import { connect } from "react-redux";
import clsx from "clsx";
import { PELIAS_API } from "../../utils/axios";

import { setProject } from "../../store/actions/project";
import { setSolution } from "../../store/actions/solution";
import {
  setExtendableId,
  setExtendableType,
  setExtendableBackTo
} from "../../store/actions/edit";
import { setHoverStop } from "../../store/actions/map";

import ConfirmDialog from "../dialogs/ConfirmDialog";
import { List } from "react-virtualized";
import addStopImg from "../../assets/images/add-stop.svg";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import {
  setInfoDialog,
  setStopSearchMode,
  setStopSelectMode
} from "../../store/actions/common";
import Skeleton from "@material-ui/lab/Skeleton";
import { deleteStop, handleStopAdd } from "../../store/actions/custom";
import AddIcon from "@material-ui/icons/Add";
import StopsToolbar from "./StopsToolbar";
import { FormattedText } from "../common/FormattedText";
import useStopsFilter from "../../hooks/useStopsFilter";
import { setLoadingProject } from "../../store/actions/loading";
import StopRow from "./StopRow";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";
import SearchBoxMessage from "../common/SearchBoxMessage";
import Axios, { CancelToken } from "axios";
import useListKeyboardCommands from "./useListKeyboardCommands";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "#fff"
  },
  stopsList: {
    width: "100% !important",
    "& > div": {
      position: "relative",
      top: "-17px"
    }
  },
  // Temporary solution for unneccarsy unscheuled header
  stopsListUpToHideUnscheduledHeader: {
    "& > div": {
      top: "-72px"
    }
  },
  searchBoxMessage: {
    paddingTop: theme.spacing(3),
    maxWidth: "80%",
    wordBreak: "break-all",
    margin: "auto"
  },
  addBlock: {
    borderBottom: theme.variables.border
  },

  noStopTitle: {
    color: "#4D4D50",
    marginBottom: theme.spacing(1)
  },

  noStopSubtitle: {
    color: "#7D7E82"
  },

  noStopsWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: theme.spacing(8.5)
  },

  addBtn: {
    textTransform: "none",
    boxShadow: "none",
    fontWeight: "500",
    height: "40px",
    marginTop: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    "& svg": {
      color: "#fff",
      fontSize: "24px !important"
    }
  },

  addIcon: {
    cursor: "pointer",
    "&:hover svg": {
      color: "#5F6368"
    }
  },
  addIconDisabled: {
    pointerEvents: "none"
  },

  listWrapper: {
    transition: theme.transitions.create("box-shadow", { duration: 200 })
  },

  textSecondary: {
    color: theme.overrides.MuiTypography.colorTextSecondary.color
  },

  stopPlaceholderTop: {
    height: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    width: "80%",
    backgroundColor: "rgba(0,0,0,.15)"
  },
  stopPlaceholderBottom: {
    height: theme.spacing(3),
    width: "85%",
    marginBottom: theme.spacing(1.75),
    backgroundColor: "rgba(0,0,0,.15)"
  },
  searchBoxPlaceholder: {
    height: theme.spacing(5),
    width: "65%",
    marginBottom: theme.spacing(1.75),
    backgroundColor: "rgba(0,0,0,.15)"
  },
  searchBoxImportPlaceholder: {
    height: theme.spacing(5),
    width: "25%",
    marginBottom: theme.spacing(1.75),
    backgroundColor: "rgba(0,0,0,.15)"
  },
  stopPlaceholderRect: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "rgba(0,0,0,.15)",
    flexShrink: 0,
    marginBottom: theme.spacing(2),
    borderRadius: "4px"
  }
}));

function Stops({
  editId,
  stopsLength,
  project,
  setProject,
  projectExpired,
  setExtendableType,
  setExtendableId,
  projectLoadingInitial,
  setLoadingProject,
  handleStopAdd,
  saveDisabled,
  optionsExpanded,
  mapBounds,
  setInfoDialog,
  deleteStop,
  plan,
  setProjectTabValue,
  isOptimized,
  editStopLoading,
  isStopSearchMode,
  isStopSelectMode,
  setStopSearchMode,
  setStopSelectMode,
  mapCenter,
  editType,
  projectTabValue,
  setExtendableBackTo,
  setHoverStop
}) {
  const [stops, setStops] = useState({
    scheduled: [],
    unscheduled: []
  });

  const [unscheduledExpanded, setUnscheduledExpanded] = useState(true);
  const [isSearchingStops, setIsSearchingStops] = useState(false);

  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  const [currentSelection, setCurrentSelection] = useState({
    scheduled: [],
    unscheduled: []
  });

  const autoCompletedRequestCancelToken = useRef(null);

  const clearAllSelectedStops = useCallback(() => {
    setCurrentSelection({
      scheduled: [],
      unscheduled: []
    });
  }, []);

  useEffect(() => {
    setStops(
      project.solution
        ? {
            unscheduled: [...project.solution.unassigned.stops],
            scheduled: [
              ...project.solution.routes.reduce(
                (arr, route) => [
                  ...arr,
                  ...route.stops.filter(
                    stop => !["break", "start", "end"].includes(stop.type)
                  )
                ],
                []
              )
            ]
          }
        : { unscheduled: project.vrp.stops, scheduled: [] }
    );
  }, [project.vrp.stops, project.solution, unscheduledExpanded]);

  useEffect(() => {
    if (editType !== "stops" && editType !== "routes") {
      setExtendableId(null);
      setExtendableType(null);
    }
    clearAllSelectedStops(); //Callback that removes all selected stops
    setSearchInputValue("");
    setSuggestions([]);
  }, [project.id, projectTabValue]);

  // Reset stopSearchMode or selectMode ||| in the past it was a feature now it is not necessary for that we have stored these states in redux store ./ Refactor after /
  useEffect(() => {
    return () => {
      setStopSearchMode(false);
      setStopSelectMode(false);
    };
  }, []);

  useEffect(() => {
    if (editType === "unscheduled") {
      setExtendableType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editType]);

  const classes = useStyles();
  const containerRef = useRef();
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    index: null,
    stop: null
  });

  const [changesDialog, setChangesDialog] = useState({ visible: false });
  const [searchInputValue, setSearchInputValue] = useState("");

  const handleSelectStop = (id, isInSelection, isScheduled, e) => {
    e.stopPropagation();
    const field = isScheduled ? "scheduled" : "unscheduled";
    if (isInSelection) {
      setCurrentSelection({
        ...currentSelection,
        [field]: currentSelection[field].filter(elId => elId !== id)
      });
    } else {
      setCurrentSelection({
        ...currentSelection,
        [field]: [...currentSelection[field], id]
      });
    }
  };

  const listRef = useRef();

  const addStop = useCallback(() => {
    const newProject = { ...project };
    newProject.vrp.stops.push({});

    setProject(newProject);

    const container = containerRef.current.parentNode;

    setTimeout(() => {
      container.scrollTop = container.scrollHeight;

      // Focus on created input
      const inputs = container.querySelectorAll(".service-input-autocomplete");

      inputs[inputs.length - 1] &&
        inputs[inputs.length - 1].querySelector("input").focus();
    }, 100);
  }, [project, setProject, containerRef]);
  // Check limits and add stop
  const handleAdd = e => {
    e && e.preventDefault();

    if (stopsLength < plan.cap_stops_per_project) addStop();
    else
      setInfoDialog({
        visible: true,
        title: "Operation not possible",
        body: `You’ve reached the maximum number of stops that you can add to one project. If you want to add more than ${plan.cap_stops_per_project} stops per project you need to upgrade your plan.`,
        button: {
          text: "Upgrade plan",
          link: "/payment/select-plan"
        },
        mainButtonText: "No, thanks"
      });
  };

  const handleClose = () => {
    setExtendableType(null);
    setExtendableBackTo(null);
    setExtendableId(null);
    setHoverStop(null);
  };

  const editStop = useCallback(
    (id, status) => {
      setExtendableId(id);
      setExtendableType("stops", { status });
    },
    [setExtendableId, setExtendableType]
  );

  const warnEdit = useCallback(
    (id, status) => {
      if (!saveDisabled) {
        setChangesDialog({ visible: true, id, status });
      } else {
        editStop(id, status);
      }
    },
    [saveDisabled, editStop]
  );

  const focusSearchField = () => {
    const el = document.getElementById("project-searchInput");
    el && el.focus();
  };

  useEffect(() => {
    setStops(
      project.solution
        ? {
            unscheduled:
              project.solution.unassigned.stops.length > 0
                ? [...project.solution.unassigned.stops]
                : [],
            scheduled: [
              ...project.solution.routes.reduce(
                (arr, route) => [
                  ...arr,
                  ...route.stops.filter(
                    stop => !["break", "start", "end"].includes(stop.type)
                  )
                ],
                []
              )
            ]
          }
        : {
            unscheduled:
              project.vrp.stops.length > 0 ? [...project.vrp.stops] : [],
            scheduled: []
          }
    );
  }, [
    project.vrp.stops,
    project.solution,
    unscheduledExpanded,
    editStopLoading
  ]);

  // Filter non start/end stops
  const filteredStopsInitial = useMemo(
    () => ({
      unscheduled: stops.unscheduled.filter(
        stop =>
          stop.type !== "start" && stop.type !== "end" && stop.type !== "break"
      ),
      scheduled: stops.scheduled.filter(
        stop =>
          stop.type !== "start" && stop.type !== "end" && stop.type !== "break"
      )
    }),
    [stops]
  );

  // Create session for autocomplete as suggests google's billing documentation
  const session = useRef(
    new window.google.maps.places.AutocompleteSessionToken()
  );

  const [suggestions, setSuggestions] = useState([]);

  const [filteredStops, stopsInfoData] = useStopsFilter(
    searchInputValue,
    filteredStopsInitial
  );

  const unscheduledExpandedRef = useRef(unscheduledExpanded);

  const isPublished = project.publishied || project.published_once;
  const isEditMode = project.status.toLowerCase() === "optimized";
  const userLang = navigator.language || navigator.userLanguage;

  const handleSearchChange = searchVal => {
    // setTempVal(value || "");
    const isAllowedToAddStopsAfterPublished =
      !projectExpired && isPublished && isEditMode;
    // Get Google autocomplete data
    if (
      searchVal &&
      (isAllowedToAddStopsAfterPublished || !isPublished) &&
      !projectExpired
    ) {
      if (!searchInputValue) {
        unscheduledExpandedRef.current = unscheduledExpanded;
      }
      // For indicating that is canceled request and we don't need to use autocomplete
      if (autoCompletedRequestCancelToken.current) {
        autoCompletedRequestCancelToken.current.cancel("cancelled");
        autoCompletedRequestCancelToken.current = CancelToken.source();
      } else {
        autoCompletedRequestCancelToken.current = CancelToken.source();
      }

      setIsSearchingStops(true);
      setUnscheduledExpanded(true);

      PELIAS_API.get("v1/autocomplete", {
        params: {
          lang: userLang ? userLang : "en", // browser language needs to be provided based on user browser
          size: 5, // number of results to return
          layers: "address,street", // type of results to return
          "focus.point.lat": `${
            mapCenter?.lat
              ? mapCenter.lat
              : (mapBounds._southWest.lat + mapBounds._northEast.lat) / 2
          }`, // focus point latitude needs to be provided based on user map showing
          "focus.point.lon": `${
            mapCenter?.lng
              ? mapCenter.lng
              : (mapBounds._southWest.lng + mapBounds._northEast.lng) / 2
          }`, // focus point longtitude needs to be provided based on user map showPELIAS_ing
          text: `${searchVal}`
        },
        timeout: 3000,
        cancelToken: autoCompletedRequestCancelToken.current.token
      })
        .then(function ({ features }) {
          // for testing
          // throw Error("Testing backup");
          // From Pelias we get array of features instead of suggestions (before self hosted Pelias our service was and now it is our backup service google autocomplete);
          setSuggestions(
            features
              ? features.map(feature => {
                  // Formatted address splited
                  const address = feature?.properties?.label.split(",");
                  return {
                    el: feature,
                    info: [
                      <FormattedText query={searchVal} text={address[0]} />,
                      <FormattedText
                        query={searchVal}
                        //Show formatted adress in bottom of street
                        text={`${address.slice(1)}`}
                      />
                    ]
                  };
                })
              : []
          );
        })
        .catch(error => {
          // Cause Pelias is self hosted sometimes in server can something went wrong and self hosted Pelias
          // can't give us suggestions for adding we need a backup for smooth user experience
          // In short -> Get suggestions from Pelias if request failed, request from google autocomplete;
          //After canceling request we don't need use autocomplete!
          if (error.message === "cancelled") {
            return;
          }
          const autocomplete =
            new window.google.maps.places.AutocompleteService();
          autocomplete.getPlacePredictions(
            {
              input: searchVal,
              sessionToken: session.current,
              // Suggest addresses from map's current boundaries on window
              bounds: {
                north: mapBounds._northEast && mapBounds._northEast.lat,
                south: mapBounds._southWest && mapBounds._southWest.lat,
                west: mapBounds._southWest && mapBounds._southWest.lng,
                east: mapBounds._northEast && mapBounds._northEast.lng
              },
              types: ["address"]
            },
            function (results) {
              setSuggestions(
                results
                  ? results.map(suggestion => ({
                      el: suggestion,
                      info: [
                        <FormattedText
                          query={searchVal}
                          text={suggestion.description?.split(",")[0]}
                        />,
                        <FormattedText
                          query={searchVal}
                          text={suggestion.description
                            ?.split(",")
                            .slice(1)
                            .join(",")}
                        />
                      ]
                    }))
                  : []
              );
            }
          );
        });
    } else {
      setUnscheduledExpanded(unscheduledExpandedRef.current);
      setSuggestions([]);
    }
    setSearchInputValue(searchVal);
  };

  const handleItemSelect = selectedItem => {
      if (selectedItem && selectedItem.place_id) {
        // Get place details
        const service = new window.google.maps.places.PlacesService(
          window.document.createElement("div")
        );
        service.getDetails(
          {
            placeId: selectedItem.place_id,
            language: "en",
            fields: ["address_components", "geometry", "formatted_address"]
          },
          function (PlaceResult, PlacesServiceStatus) {
            setLoadingProject(true);
            const body = { address: PlaceResult, id: null };
            // Post service changes
            handleStopAdd(body, {
              stopsLength,
              track: true,
              callback: () => {
                focusSearchField();
                setSuggestions([]);
                setSearchInputValue("");
              }
            });
          }
        );
      } else {
        setLoadingProject(true);
        const feature = selectedItem;

        const splitedAddress = feature.properties.label.split(",");

        const newFormatedAddress =
          splitedAddress[0] +
          ", " +
          feature?.properties?.country +
          ", " +
          feature?.properties?.country_code +
          ", " +
          splitedAddress.slice(2);

        const addressComponents = [
          {
            long_name:
              feature?.properties?.street ||
              feature.properties.label.split(",")[0], // feature?.properties?.street
            short_name:
              feature?.properties?.street ||
              feature.properties.label.split(",")[0], // feature?.properties?.street
            types: ["route"]
          },
          feature?.properties?.country &&
            feature?.properties?.country_code && {
              long_name: feature?.properties?.country, // feature?.properties?.country
              short_name: feature?.properties?.country_code, // feature?.properties?.country_code
              types: ["country", "political", "postal_town"]
            },
          feature?.properties?.housenumber && {
            long_name: feature?.properties?.housenumber, // feature?.properties?.housenumber
            short_name: feature?.properties?.housenumber, // feature?.properties?.housenumber
            types: ["street_number"]
          },
          feature?.properties?.borough && {
            long_name: feature?.properties?.borough, // feature?.properties?.borough
            short_name: feature?.properties?.borough, // feature?.properties?.borough
            types: ["sublocality_level_1", "sublocality", "political"]
          },
          feature?.properties?.locality && {
            long_name: feature?.properties?.locality, // feature?.properties?.locality
            short_name: feature?.properties?.locality, // feature?.properties?.locality
            types: ["locality", "political"]
          },
          feature?.properties?.region &&
            feature?.properties?.region_a && {
              long_name: feature?.properties?.region, // feature?.properties?.region
              short_name: feature?.properties?.region_a, // feature?.properties?.region_a
              types: ["administrative_area_level_1", "political"]
            },
          feature?.properties?.locality && {
            long_name: feature?.properties?.locality, // feature?.properties?.locality
            short_name: feature?.properties?.locality, // feature?.properties?.locality
            types: ["administrative_area_level_2", "political"]
          },
          feature?.properties?.postalcode && {
            long_name: feature?.properties?.postalcode, // feature?.properties?.postalcode
            short_name: feature?.properties?.postalcode, // feature?.properties?.postalcode
            types: ["postal_code"]
          }
        ].filter(Boolean);

        const body = {
          address: {
            address_components: addressComponents,
            formatted_address: feature?.properties?.label,
            geometry: {
              location: {
                lng: feature.geometry.coordinates[0],
                lat: feature.geometry.coordinates[1]
              }
            }
          },
          id: null
        };

        // const body = {
        //   stops: [
        //     {
        //       type: "delivery",
        //       street: feature.properties?.street
        //         ? `${feature.properties?.street} ${
        //             feature.properties?.housenumber
        //               ? feature.properties?.housenumber
        //               : ""
        //           }`
        //         : null,
        //       city: feature.properties?.locality
        //         ? `${feature.properties?.locality}`
        //         : null,
        //       postal_code: feature.properties?.postalcode
        //         ? feature.properties?.postalcod
        //         : null,
        //       country_code: feature?.properties?.country_code,
        //       long: feature.geometry?.coordinates[0],
        //       lat: feature.geometry?.coordinates[1]
        //     }
        //   ]
        // };

        // // Post service changes
        handleStopAdd(body, {
          stopsLength,
          track: true,
          callback: () => {
            focusSearchField();
            setSuggestions([]);
            setSearchInputValue("");
          }
        });
      }
  };

  useEffect(() => {
    setIsSearchingStops(false);
  }, [suggestions]);

  useEffect(() => {
    if (searchInputValue.trim().length > 0) {
      !isStopSearchMode && setStopSearchMode(true);
    } else {
      isStopSearchMode && setStopSearchMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputValue]);

  useEffect(() => {
    listKeyboardCommands.methods.setListSize(
      filteredStops.scheduled.length +
        filteredStops.unscheduled.length +
        suggestions.length
    );
  }, [filteredStops, suggestions, listKeyboardCommands.methods]);

  useEffect(() => {
    if (searchInputValue === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.reset();
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, searchInputValue]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    if (index < filteredStops.unscheduled.length) {
      const stop = filteredStops.unscheduled[index];
      warnEdit(stop.id, stop.status);
    } else if (
      index <
      filteredStops.scheduled.length + filteredStops.unscheduled.length
    ) {
      const stop =
        filteredStops.scheduled[index - filteredStops.unscheduled.length];
      warnEdit(stop.id, stop.status);
    } else {
      const suggestion =
        suggestions[
          index -
            filteredStops.scheduled.length -
            filteredStops.unscheduled.length
        ];
      handleItemSelect(suggestion.el);
    }
  });

  const filteredStopsLength =
    (unscheduledExpanded ? filteredStops.unscheduled.length : 0) +
    filteredStops.scheduled.length;

  function rowRenderer({
    key, // Unique key within array of rows
    index, // Index of row within collection
    style // Style object to be applied to row (to position it)
  }) {
    return (
      <StopRow
        key={key}
        index={index}
        style={style}
        solution={project.solution}
        filteredStops={filteredStops}
        filteredStopsLength={filteredStopsLength}
        currentSelection={currentSelection}
        handleSelectStop={handleSelectStop}
        projectExpired={projectExpired}
        stopsLength={stopsLength}
        warnEdit={warnEdit}
        handleAdd={handleAdd}
        stopsInfoData={stopsInfoData}
        unscheduledExpanded={unscheduledExpanded}
        suggestions={suggestions}
        editId={editId}
        handleItemSelect={handleItemSelect}
        setUnscheduledExpanded={setUnscheduledExpanded}
        setHaveHovered={setHaveHovered}
        haveHovered={haveHovered}
        activeByListCommandCurrentIndex={listKeyboardCommands.currentIndex}
      />
    );
  }

  const listWrapperRef = useRef();

  const productTour = useProductTour();
  const isProductTourPartAddingStopsAndOptimizeStepAddSearchAndOptimize =
    productTour.isTourStepId(
      PRODUCT_TOUR_STEP_IDS.PART_ADDING_STOPS_AND_OPTIMIZE_STEP_SEARCH_AND_OPTIMIZE
    );

  useEffect(() => {
    if (
      stopsLength >= 2 &&
      isProductTourPartAddingStopsAndOptimizeStepAddSearchAndOptimize
    ) {
      if (isStopSearchMode) {
        productTour.hidePulsingBorder();
      } else {
        productTour.showPulsingBorder();
      }
    }
  }, [
    isProductTourPartAddingStopsAndOptimizeStepAddSearchAndOptimize,
    productTour,
    stopsLength,
    isStopSearchMode
  ]);

  const listLength =
    // stops to show length
    filteredStopsLength +
    // headers to show length
    (project.solution ? 2 : 1) -
    // minus the headers with 0 stops
    Number(!filteredStops.unscheduled.length) -
    (project.solution ? Number(!filteredStops.scheduled.length) : 0) +
    // plus suggestions length + suggestions header
    (suggestions.length > 0 ? suggestions.length + 1 : 0);

  const numSelected =
    currentSelection.scheduled.length + currentSelection.unscheduled.length;

  // When we need to calculate list height there also two cases when we have bottom bar or don't have .
  const listHeight = Math.max(
    Math.min(
      listLength * 56,
      window.innerHeight -
        (isStopSearchMode || isStopSelectMode ? 320 : !isOptimized ? 230 : 140)
    ),
    56
  );
  // Is it collapsing with bottom button[type @boolean] -> so when we have not bottom bar we need to calculate accourding to optimize button position elsewhere for reoptimize button.
  const isListCollapsingWithBottomButton =
    listHeight > window.innerHeight - (!isOptimized ? 5 * 56 : 6 * 56);

  numSelected
    ? !isStopSelectMode && setStopSelectMode(true)
    : isStopSelectMode && setStopSelectMode(false);

  const optimizeListHeight = () => {
    return listHeight > window.innerHeight - 320 && !isOptimized
      ? listHeight + 60
      : !stops.scheduled.length &&
        !(isStopSelectMode || isStopSearchMode) &&
        isOptimized &&
        isListCollapsingWithBottomButton
      ? listHeight - 70
      : isListCollapsingWithBottomButton && listHeight < window.innerHeight
      ? window.innerHeight - 270
      : isStopSelectMode || isStopSearchMode
      ? listHeight + 112
      : listHeight;
  };

  return (
    <>
      <Slide
        direction="up"
        in={!optionsExpanded}
        id="project_drawer_stops_wrapper"
      >
        <Box mb={0} className={classes.root} ref={containerRef}>
          {!projectLoadingInitial ? (
            <StopsToolbar
              setChangesDialog={setChangesDialog}
              searchInputValue={searchInputValue}
              stopsLength={stopsLength}
              stops={stops}
              selectedStops={currentSelection}
              clearAllSelectedStops={clearAllSelectedStops}
              setLoadingProject={setLoadingProject}
              setSearchInputValue={handleSearchChange}
              numSelected={numSelected}
              allStopsSelected={
                numSelected === filteredStopsLength ||
                numSelected === stopsLength
              }
              handleSelectAllClick={event => {
                if (event.target.checked) {
                  const newSelected = {
                    scheduled: filteredStops.scheduled.map(el => el.id),
                    unscheduled: filteredStops.unscheduled.map(el => el.id)
                  };
                  setCurrentSelection(newSelected);
                  return;
                }
                clearAllSelectedStops();
              }}
            />
          ) : (
            <Box display="flex" justifyContent={"space-evenly"} pt={2}>
              <Skeleton
                className={classes.searchBoxPlaceholder}
                variant="rect"
              />
              <Skeleton
                variant="rect"
                className={classes.searchBoxImportPlaceholder}
              />
            </Box>
          )}

          {/* Virtualized list */}
          <Box
            className={classes.listWrapper}
            ref={listWrapperRef}
            boxShadow={"inset 0 1px 3px -1px rgba(0,0,0,0.0)"}
          >
            {isSearchingStops ? (
              <SearchBoxMessage
                message="Searching for stops..."
                className={classes.searchBoxMessage}
              />
            ) : project.project_date && !projectLoadingInitial ? (
              listLength - 1 > 0 || !unscheduledExpanded ? (
                <List
                  // Because when our stops is not optimize bottom bar there is not so we need to add 60px to height for scrolling till the end ot user window
                  height={optimizeListHeight()}
                  rowCount={listLength}
                  rowHeight={56}
                  rowRenderer={rowRenderer}
                  width={448}
                  scrollToAlignment="start"
                  ref={listRef}
                  onScroll={e => {
                    if (listWrapperRef.current) {
                      if (e.scrollTop > 0) {
                        listWrapperRef.current.style.boxShadow =
                          "inset 0 1px 3px -1px rgba(0,0,0,0.2)";
                      } else {
                        listWrapperRef.current.style.boxShadow =
                          "inset 0 1px 3px -1px rgba(0,0,0,0.0)";
                      }
                    }
                  }}
                  // It is temporary solution !!! When we have solution we have an issue and have not unscheduled list we had issue we render unnecessary header cause there is not unscheduled but now we can not change this wrong logic so best solution is just hide unnecessary row
                  className={clsx(classes.stopsList)}
                  style={
                    !isStopSelectMode && !isStopSearchMode
                      ? {
                          // We adding a padding for button space when a user scrolles till the end we need to optimize or reoptimize don't collapse with stops
                          paddingBottom: `${
                            !isOptimized
                              ? 110
                              : isEditMode || (isOptimized && !isPublished)
                              ? listHeight > window.innerHeight - 380
                                ? 80
                                : 200
                              : 0
                          }px`,
                          // Overflow auto added for stops when they space is collapsing with optimize / reoptimize button .
                          overflowY: isListCollapsingWithBottomButton
                            ? "auto"
                            : "hidden"
                        }
                      : {
                          overflowY:
                            listHeight > window.innerHeight - 380
                              ? "auto"
                              : "hidden",
                          paddingBottom: isStopSelectMode ? "60px" : "0"
                        }
                  }
                  scrollToIndex={listKeyboardCommands.currentIndex}
                />
              ) : !searchInputValue.trim() ? (
                <div className={classes.noStopsWrapper}>
                  <img src={addStopImg} alt="" />
                  <Typography
                    className={classes.noStopTitle}
                    variant="body1"
                    data-id="projectStops-emptyStopList"
                  >
                    You haven’t added any stops to this project
                  </Typography>
                  <Typography
                    className={classes.noStopSubtitle}
                    variant="body2"
                  >
                    Once you add a stop, it will show up here.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.addBtn}
                    disableElevation
                    onClick={focusSearchField}
                    startIcon={<AddIcon />}
                    id="new_stop_button"
                  >
                    Add stop
                  </Button>
                </div>
              ) : (
                !isSearchingStops &&
                suggestions.length < 1 && (
                  <SearchBoxMessage
                    message={`No stops found for "${searchInputValue}"`}
                    className={classes.searchBoxMessage}
                  />
                )
              )
            ) : (
              <Box p={3}>
                {new Array(3).fill("").map((v, i) => (
                  <Box display="flex" key={i} alignItems="center">
                    <Skeleton
                      variant="rect"
                      className={classes.stopPlaceholderRect}
                    />
                    <Box width="100%" flexShrink={1} ml={2}>
                      <Skeleton
                        className={classes.stopPlaceholderTop}
                        variant="rect"
                      />
                      <Skeleton
                        className={classes.stopPlaceholderBottom}
                        variant="rect"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Slide>

      <ConfirmDialog
        open={confirmDialogState.open}
        handleClose={() => setConfirmDialogState({ open: false })}
        action={() => deleteStop(confirmDialogState.stop)}
        title={"Delete stop?"}
        body={`Deleting a stop is permanent and cannot be undone.`}
      />
      <UnsavedChangesDialog
        open={changesDialog.visible}
        action={() => handleClose()}
        handleClose={() => setChangesDialog({ visible: false })}
      />
    </>
  );
}

function mapStateToProps(state) {
  return {
    project: state.project,
    editId: state.extendable.id,
    editStopLoading: state.loading.editStop,
    projectLoadingInitial: state.loading.projectInitial,
    userType: state.auth.userData.userType,
    saveDisabled: state.extendable.saveDisabled,
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {},
    projectExpired: state.project.status.toLowerCase() === "expired",
    mapBounds: state.map.bounds,
    routes: state.project.solution ? state.project.solution.routes : [],
    isOptimized: state.project.solution,
    isEditMode: state.project.status === "Optimized",
    isPublished: state.project.published_once,
    isStopSearchMode: state.common.isStopSearchMode,
    isStopSelectMode: state.common.isStopSelectMode,
    mapCenter: state.map.mapCenter,
    editType: state.extendable.type,
    projectTabValue: state.common.projectTabValue
  };
}

export default connect(mapStateToProps, {
  setProject,
  setExtendableType,
  setExtendableId,
  setLoadingProject,
  handleStopAdd,
  setInfoDialog,
  deleteStop,
  setSolution,
  setStopSearchMode,
  setStopSelectMode,
  setExtendableBackTo,
  setHoverStop
})(Stops);
